import { useEffect, useState } from "react";
import {
  FaSnowflake,
  FaMapMarkerAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaThermometerHalf,
  FaEdit,
  FaTrash,
  FaHistory
} from "react-icons/fa";
import {
  getAllChambresFroides,
  createChambreFroide,
  updateChambreFroide,
  deleteChambreFroide,
  getHistoriqueChambreFroide
} from "../../services/chambresfroides";
import { getReleves } from "../../services/releves";
import { getSeuils } from "../../services/seuils";
import { getAllEntrepots } from "../../services/entrepots";
import type { ChambreFroide, ChambreFroidePayload } from "../../types/chambrefroide";
import type { HistoriqueArretChambreFroide } from "../../types/historiquearretchambre";
import type { Releve } from "../../types/releve";
import type { Seuil } from "../../types/seuil";
import type { Entrepot } from "../../types/entrepot";
import "./ChambresFroides.css";

export default function ChambresFroides() {
  const [chambres, setChambres] = useState<ChambreFroide[]>([]);
  const [releves, setReleves] = useState<Releve[]>([]);
  const [seuils, setSeuils] = useState<Seuil[]>([]);
  const [entrepots, setEntrepots] = useState<Entrepot[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedChambre, setSelectedChambre] = useState<ChambreFroide | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyChambre, setHistoryChambre] = useState<ChambreFroide | null>(null);
  const [historiques, setHistoriques] = useState<HistoriqueArretChambreFroide[]>([]);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const safeSetArray = <T,>(setter: React.Dispatch<React.SetStateAction<T[]>>) => (data: unknown) => {
      if (Array.isArray(data)) setter(data as T[]);
      else setter([]);
    };

    const fetchData = async () => {
      const chambresData = await getAllChambresFroides();
      const relevesData = await getReleves();
      const seuilsData = await getSeuils();
      const entrepotsData = await getAllEntrepots();
      safeSetArray<ChambreFroide>(setChambres)(chambresData);
      safeSetArray<Releve>(setReleves)(relevesData);
      safeSetArray<Seuil>(setSeuils)(seuilsData);
      safeSetArray<Entrepot>(setEntrepots)(entrepotsData);
    };
    fetchData();
  }, []);


  const getDerniereReleve = (chambreId: string): Releve | null => {
    const chambreReleves = releves.filter(r => r.chambre_froide.id === chambreId);
    return chambreReleves.length > 0 ? chambreReleves[chambreReleves.length - 1] : null;
  };

  const getSeuilByChambre = (chambreId: string): Seuil | null => {
    const chambreSeuils = seuils.filter(s => s.chambre_froide.id === chambreId);
    return chambreSeuils.length > 0 ? chambreSeuils[0] : null;
  };

  const getEtatTemperature = (temperature: number | undefined, seuil: Seuil | null) => {
    if (temperature === undefined || seuil === null) return "inconnu";
    if (seuil.temperature_min !== undefined && seuil.temperature_max !== undefined) {
      if (temperature >= seuil.temperature_min && temperature <= seuil.temperature_max) {
        return "bon";
      }
    }
    return "mauvais";
  };

  const getMoyenneTemperature = (chambreId: string): number | null => {
    const chambreReleves = releves.filter(r => r.chambre_froide.id === chambreId && r.temperature !== undefined);
    if (chambreReleves.length === 0) return null;

    const total = chambreReleves.reduce((sum, r) => sum + (r.temperature ?? 0), 0);
    return total / chambreReleves.length;
  };

  const tempsDepuisDerniereReleve = (releve: Releve | null) => {
    if (!releve) return "aucune donnée";
    const dateReleve = new Date(`${releve.date}T${releve.heure}`);
    const diffMs = now - dateReleve.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return `${Math.floor(diffMs / 1000)} sec`;
    if (diffMin < 60) return `${diffMin} min`;
    const diffH = Math.floor(diffMin / 60);
    return `${diffH} h`;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const payload: ChambreFroidePayload = {
      nom: formData.get("nom") as string,
      capacite: parseFloat(formData.get("capacite") as string),
      occupation: parseFloat(formData.get("occupation") as string),
      entrepot_id: formData.get("entrepot") as string,
      description: (formData.get("description") as string) || "",
      etat: formData.get("etat") === "on",
      commentaire_hors_service: (formData.get("commentaire_hors_service") as string) || ""
    };

    if (selectedChambre) {
      await updateChambreFroide(selectedChambre.id, payload);
    } else {
      await createChambreFroide(payload);
    }

    const data = await getAllChambresFroides();
    setChambres(data);
    setShowModal(false);
    setSelectedChambre(null);
  };


  const handleDelete = async (id: string) => {
    if (window.confirm("Supprimer cette chambre froide ?")) {
      await deleteChambreFroide(id);
      setChambres(chambres.filter(c => c.id !== id));
    }
  };

  const handleShowHistory = async (chambre: ChambreFroide) => {
    setHistoryChambre(chambre);
    const data = await getHistoriqueChambreFroide(chambre.id);
    setHistoriques(data);
    setShowHistoryModal(true);
  };

  return (
    <div className="chambres">
      <div className="chambres-header">
        <button className="btn-ajout" onClick={() => setShowModal(true)}>
          ➕ Ajouter chambre froide
        </button>
      </div>

      <div className="chambres-grid">
        {chambres.map((c) => {
          const moyenneTemperature = getMoyenneTemperature(c.id);
          const seuil = getSeuilByChambre(c.id);
          const etatTemperature = getEtatTemperature(moyenneTemperature ?? undefined, seuil);
          const occupationPourcentage = c.capacite > 0 ? Math.round((c.occupation / c.capacite) * 100) : 0;

          return (
            <div key={c.id} className="chambre-card">
              <h3 className="badge-chambre">
                <FaSnowflake className="icon-badge" /> {c.nom}
              </h3>
              <p className="card-line"><FaMapMarkerAlt className="icon-line" /> {c.entrepot.nom}</p>
              <p className="card-line">{c.etat ? <span className="etat-on">En service</span> : <span className="etat-off">Hors service</span>}</p>

              <div className={`status-container ${etatTemperature === "bon" ? "status-good" : "status-bad"}`}>
                {etatTemperature === "bon" ? (<><FaCheckCircle /> <strong>Optimal</strong></>) : (<><FaTimesCircle /> <strong>Mauvais</strong></>)}
                <span className="update-time">Mise à jour : {tempsDepuisDerniereReleve(getDerniereReleve(c.id))}</span>
              </div>

              <p className="card-line"><FaThermometerHalf className="icon-line" /> {moyenneTemperature?.toFixed(1) ?? "--"} °C</p>

              <div className="occupation-bar">
                <div className="occupation-fill" style={{ width: `${occupationPourcentage}%` }}></div>
              </div>
              <p>{c.occupation} / {c.capacite} tonnes ({occupationPourcentage}%)</p>

              <div className="card-actions">
                <FaEdit className="icon-edit" onClick={() => { setSelectedChambre(c); setShowModal(true); }} />
                <FaTrash className="icon-trash" onClick={() => handleDelete(c.id)} />
                <FaHistory className="icon-history" onClick={() => handleShowHistory(c)} />
              </div>
            </div>
          );
        })}
      </div>

      {/* ✅ Modale ajout/modification */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{selectedChambre ? "Modifier chambre froide" : "Ajouter chambre froide"}</h3>
            <form onSubmit={handleSubmit}>
              <input
                type="text"
                name="nom"
                placeholder="Nom de la chambre"
                defaultValue={selectedChambre?.nom ?? ""}
                required
              />

              <input
                type="number"
                name="capacite"
                placeholder="Capacité (tonnes)"
                defaultValue={selectedChambre?.capacite ?? ""}
                required
              />

              <input
                type="number"
                name="occupation"
                placeholder="Occupation (tonnes)"
                defaultValue={selectedChambre?.occupation ?? ""}
                required
              />

              <textarea
                name="description"
                placeholder="Description"
                defaultValue={selectedChambre?.description ?? ""}
              ></textarea>

              {/* ✅ case En service */}
              <label>
                En service
                <input
                  type="checkbox"
                  name="etat"
                  defaultChecked={selectedChambre?.etat ?? true}
                  onChange={(e) => {
                    if (selectedChambre) {
                      setSelectedChambre({ ...selectedChambre, etat: e.target.checked });
                    }
                  }}
                />
              </label>

              {/* ✅ champ commentaire obligatoire si hors-service */}
              {selectedChambre && !selectedChambre.etat && (
                <textarea
                  name="commentaire_hors_service"
                  placeholder="Raison de la mise hors-service (obligatoire)"
                  defaultValue={selectedChambre?.commentaire_hors_service ?? ""}
                  required
                ></textarea>
              )}

              {/* ✅ Sélection de l'entrepôt */}
              <select
                name="entrepot"
                defaultValue={selectedChambre?.entrepot?.id ?? ""}
                required
              >
                <option value="">-- Choisir un entrepôt --</option>
                {entrepots.length > 0 ? (
                  entrepots.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.nom}
                    </option>
                  ))
                ) : (
                  <option disabled>Aucun entrepôt disponible</option>
                )}
              </select>

              <div className="modal-actions">
                <button type="submit">Enregistrer</button>
                <button type="button" onClick={() => setShowModal(false)}>
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ✅ Modale d’historique */}
      {showHistoryModal && historyChambre && (
        <div className="modal-overlay" onClick={() => setShowHistoryModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Historique des arrêts – {historyChambre.nom}</h3>
            {historiques.length > 0 ? (
              <ul className="modal-body">
                {historiques.map((h) => (
                  <li key={h.id}>
                    <span className="history-text">
                      📅 {new Date(h.date_arret).toLocaleString()}
                      {h.commentaire && <> – 📝 {h.commentaire}</>}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="history-text">Aucun arrêt enregistré.</p>
            )}
            <div className="modal-actions">
              <button type="button" onClick={() => setShowHistoryModal(false)}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

